import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setCurrentDoc, removeDoc } from '../../store/knowledgeBaseSlice';
import { knowledgeBaseAPI, projectAPI } from '../../api/services';
import { KnowledgeBaseDoc, User } from '../../types';
import BlockRenderer from './blocks/BlockRenderer';
import KBCommentSection from './KBCommentSection';

const VISIBILITY_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  private: { label: 'Private', color: 'bg-gray-100 text-gray-600', icon: '🔒' },
  department: { label: 'Department', color: 'bg-blue-100 text-blue-700', icon: '🏢' },
  team: { label: 'Team', color: 'bg-purple-100 text-purple-700', icon: '👥' },
  public: { label: 'Public', color: 'bg-green-100 text-green-700', icon: '🌐' },
};

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

const KnowledgeBaseViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s: RootState) => s.auth);
  const { currentDoc } = useSelector((s: RootState) => s.knowledgeBase);

  const [doc, setDoc] = useState<KnowledgeBaseDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [deleting, setDeleting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    setLoading(true);
    knowledgeBaseAPI.get(id)
      .then((data) => {
        if (!mounted) return;
        setDoc(data);
        dispatch(setCurrentDoc(data));
      })
      .catch(() => navigate('/knowledge-base'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id, navigate, dispatch]);

  const userId = user?._id ?? user?.id ?? '';

  const canEdit = doc && user && (
    doc.createdBy === userId ||
    (doc.collaborators ?? []).some((c: any) => c.userId === userId) ||
    user.role === 'admin' ||
    (user.organizationRole?.priority ?? 0) >= 90
  );

  const isOwner = doc && user && doc.createdBy === userId;

  const handleDelete = async () => {
    if (!id || !window.confirm('Delete this document permanently?')) return;
    setDeleting(true);
    try {
      await knowledgeBaseAPI.delete(id);
      dispatch(removeDoc(id));
      navigate('/knowledge-base');
    } catch { /* ignore */ }
    finally { setDeleting(false); }
  };

  const handlePrint = () => window.print();

  const openCollabModal = async () => {
    setShowCollabModal(true);
    if (allUsers.length === 0) {
      try {
        const res = await projectAPI.getProjectUsers();
        setAllUsers(res.data ?? res ?? []);
      } catch { /* ignore */ }
    }
  };

  const addCollaborator = async (u: User) => {
    if (!id) return;
    const uid = u._id ?? u.id ?? '';
    if (!uid) return;
    // already a collaborator?
    if ((doc?.collaborators ?? []).some((c: any) => c.userId === uid)) return;
    try {
      await knowledgeBaseAPI.addCollaborator(id, uid, u.name ?? '');
      const updated = await knowledgeBaseAPI.get(id);
      setDoc(updated);
      setUserSearch('');
    } catch { /* ignore */ }
  };

  const removeCollaborator = async (collaboratorId: string) => {
    if (!id) return;
    try {
      await knowledgeBaseAPI.removeCollaborator(id, collaboratorId);
      const updated = await knowledgeBaseAPI.get(id);
      setDoc(updated);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!doc) return null;

  const vis = VISIBILITY_LABELS[doc.visibility] ?? { label: doc.visibility, color: 'bg-gray-100 text-gray-600', icon: '' };

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar — hidden on print */}
      <div className="print:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/knowledge-base')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Knowledge Base
          </button>
          <span className="text-gray-200">/</span>
          <span className="text-sm text-gray-700 font-medium truncate max-w-xs">{doc.title || 'Untitled'}</span>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Print / Save as PDF"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>

            {canEdit && (
              <>
                {isOwner && (
                  <button
                    onClick={openCollabModal}
                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Share
                  </button>
                )}

                <button
                  onClick={() => navigate(`/knowledge-base/${id}/edit`)}
                  className="flex items-center gap-1.5 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>

                {isOwner && (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-sm text-red-500 hover:text-red-700 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Document */}
      <div ref={printRef} className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="space-y-3 mb-10">
          <div className="text-5xl">{doc.icon || '📄'}</div>
          <h1 className="text-4xl font-bold text-gray-900 leading-tight">{doc.title || 'Untitled'}</h1>
          {doc.description && <p className="text-lg text-gray-500">{doc.description}</p>}

          <div className="flex flex-wrap items-center gap-3 pt-2 text-sm text-gray-500">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${vis.color}`}>
              {vis.icon} {vis.label}
            </span>
            {doc.updatedAt && <span>Updated {timeAgo(doc.updatedAt)}</span>}
            {(doc.viewCount ?? 0) > 0 && <span>{doc.viewCount} views</span>}
            {(doc.tags ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {doc.tags!.map((tag) => (
                  <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Blocks */}
        <div className="space-y-3">
          {(doc.blocks ?? []).map((block) => (
            <div key={block.id} id={`kb-block-${block.id}`}>
              <BlockRenderer
                block={block}
                isEditing={false}
                onChange={() => {}}
                onFinish={() => {}}
                allBlocks={doc.blocks}
              />
            </div>
          ))}
          {(doc.blocks ?? []).length === 0 && (
            <p className="text-gray-300 italic">This document has no content yet.</p>
          )}
        </div>

        {/* Comments */}
        <div className="mt-16 pt-8 border-t border-gray-100 print:hidden">
          <KBCommentSection docId={doc._id} />
        </div>
      </div>

      {/* Collaborator modal */}
      {showCollabModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCollabModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Collaborators</h2>
              <button onClick={() => setShowCollabModal(false)} className="text-gray-400 hover:text-gray-700">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Current collaborators */}
            {(doc.collaborators ?? []).length > 0 ? (
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {doc.collaborators!.map((c: any) => (
                  <li key={c.userId} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs text-blue-700 font-semibold flex-shrink-0">
                        {c.name?.charAt(0).toUpperCase() ?? '?'}
                      </div>
                      <span className="text-sm text-gray-700">{c.name}</span>
                    </div>
                    <button onClick={() => removeCollaborator(c.userId)} className="text-xs text-red-400 hover:text-red-600 flex-shrink-0">Remove</button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">No collaborators yet.</p>
            )}

            {/* Search + add user */}
            <div className="space-y-2">
              <input
                autoFocus
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users by name or email…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
              />
              {userSearch.trim() && (
                <ul className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {allUsers
                    .filter((u) => {
                      const q = userSearch.toLowerCase();
                      const uid = u._id ?? u.id ?? '';
                      const alreadyAdded = (doc.collaborators ?? []).some((c: any) => c.userId === uid) || uid === userId;
                      return !alreadyAdded && (
                        u.name?.toLowerCase().includes(q) ||
                        u.email?.toLowerCase().includes(q)
                      );
                    })
                    .slice(0, 8)
                    .map((u) => (
                      <li key={u._id ?? u.id}>
                        <button
                          onClick={() => addCollaborator(u)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left transition-colors"
                        >
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-600 font-semibold flex-shrink-0">
                            {u.name?.charAt(0).toUpperCase() ?? '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                            <p className="text-xs text-gray-400 truncate">{u.email}</p>
                          </div>
                        </button>
                      </li>
                    ))}
                  {allUsers.filter((u) => {
                    const q = userSearch.toLowerCase();
                    const uid = u._id ?? u.id ?? '';
                    const alreadyAdded = (doc.collaborators ?? []).some((c: any) => c.userId === uid) || uid === userId;
                    return !alreadyAdded && (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
                  }).length === 0 && (
                    <li className="px-3 py-3 text-sm text-gray-400 text-center">No users found</li>
                  )}
                </ul>
              )}
            </div>
            <p className="text-xs text-gray-400">Collaborators can view and edit this document.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseViewer;
